// A dialog form's replies and messages (v1.3 ticket 22). Ticket 21 built this pattern inside Comments.jsx after its
// review found three defects; Raise Service Request had the same three, so the pattern is shared here and used by
// both (and by Add New Holding for its replies):
// - a reply that lands after its dialog was closed *and opened again* closed or emptied the new dialog, or marked the
//   new dialog's file control with the old file's refusal → useDialogSession: a reply touches only the opening it
//   was sent from;
// - choosing an accepted file cleared the whole alert region, including the "N required fields are empty" summary
//   while the fields were still empty; closing the dialog cleared whatever the alert region held, and left its own
//   "… removed — <name>." behind → useFormMessages: a form takes away only the messages it raised, and only while they
//   are still showing (App.js message ids).
import { useContext, useEffect, useMemo, useRef } from "react";
import { GlobalContext } from "../../store/global-context";

/**
 * Which opening of a dialog a reply belongs to: a count that moves on each time the dialog opens or closes, and when
 * the screen goes.
 *   const session = useDialogSession();
 *   open / close:  session.renew();
 *   send:          const isCurrent = session.capture();  …then(() => { if (isCurrent()) closeTheDialog(); })
 * @returns {{ renew: () => void, capture: () => () => boolean }}
 */
export function useDialogSession() {
  const count = useRef(0);
  useEffect(() => () => { count.current += 1; }, []); // the screen went: no reply belongs to it any more
  return useMemo(() => ({
    renew: () => { count.current += 1; },
    capture: () => {
      const at = count.current;
      return () => at === count.current;
    },
  }), []);
}

/**
 * The live-region messages (App.js) a form raises, kept so it can take away its own and nobody else's.
 *   messages.alert(text, about)   an error; `about` says what it is about, e.g. "form" (the required-fields summary, a
 *                                 failed send) or "file" (a refused file)
 *   messages.status(text)         a confirmation, e.g. "Supporting document chosen — <name>."
 *   messages.clearAlert(about?)   take the form's error away (only one about `about`, if given), if it is still showing
 *   messages.clearStatus()        take the form's confirmation away, if it is still showing
 * An outcome that should outlive the dialog ("Service request <ID> raised — …") goes through GlobalContext directly.
 */
export function useFormMessages() {
  const { raiseAlert, clearAlert, clearStatus } = useContext(GlobalContext);
  const ownAlert = useRef(null); // { id, about } of the form's error in the alert region
  const ownStatus = useRef(null); // id of the form's confirmation in the status region
  // The screen went (browser Back with the dialog open): its form's messages go with it, as when the dialog closes
  // (review of 22: the "required fields are empty" summary and "… chosen — <name>." stayed on the next page)
  useEffect(() => () => {
    if (ownAlert.current) clearAlert(ownAlert.current.id);
    if (ownStatus.current !== null) clearStatus(ownStatus.current);
  }, [clearAlert, clearStatus]);
  return useMemo(() => ({
    alert(text, about) {
      ownAlert.current = { id: raiseAlert(text, "error"), about };
    },
    status(text) {
      ownStatus.current = raiseAlert(text, "success");
    },
    clearAlert(about) {
      const own = ownAlert.current;
      if (!own || (about !== undefined && own.about !== about)) return;
      clearAlert(own.id);
      ownAlert.current = null;
    },
    clearStatus() {
      if (ownStatus.current === null) return;
      clearStatus(ownStatus.current);
      ownStatus.current = null;
    },
  }), [raiseAlert, clearAlert, clearStatus]);
}
